import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  Bell, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  BellOff,
  CheckCheck
} from 'lucide-react';
import { getLocalDateTimeString } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Notifications() {
  const { getAuthHeaders } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/notifications`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setNotifications(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await fetch(`${API_URL}/api/notifications/${notificationId}/read`, {
        method: 'PUT',
        credentials: 'include',
        headers: getAuthHeaders()
      });
      
      setNotifications(notifications.map(n => 
        n.notification_id === notificationId ? { ...n, is_read: true } : n
      ));
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await fetch(`${API_URL}/api/notifications/read-all`, {
        method: 'PUT',
        credentials: 'include',
        headers: getAuthHeaders()
      });
      
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'task_approved':
        return <CheckCircle2 className="w-6 h-6 text-green-500" />;
      case 'task_rejected':
        return <XCircle className="w-6 h-6 text-red-500" />;
      case 'new_submission':
        return <Clock className="w-6 h-6 text-yellow-500" />;
      default:
        return <Bell className="w-6 h-6 text-[#08513F]" />;
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-[#08513F] border-t-transparent rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#264653] font-outfit">Notifications</h1>
            <p className="text-[#6B7280] mt-1">
              {unreadCount > 0 ? `${unreadCount} unread notifications` : 'All caught up!'}
            </p>
          </div>

          {unreadCount > 0 && (
            <Button
              onClick={markAllAsRead}
              variant="outline"
              className="rounded-full border-2 border-[#08513F] text-[#08513F]"
              data-testid="mark-all-read-btn"
            >
              <CheckCheck className="w-4 h-4 mr-2" />
              Mark All as Read
            </Button>
          )}
        </div>

        {/* Notifications List */}
        {notifications.length === 0 ? (
          <Card className="bg-white rounded-3xl border-[#D4AF37]/20 shadow-lg p-12 text-center">
            <BellOff className="w-16 h-16 text-[#D4AF37]/50 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-[#264653] font-outfit mb-2">No Notifications</h2>
            <p className="text-[#6B7280]">You're all caught up! Check back later for updates.</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {notifications.map((notification, index) => (
              <Card 
                key={notification.notification_id}
                className={`rounded-2xl border-[#D4AF37]/20 shadow-md hover:shadow-lg transition-all cursor-pointer animate-slide-up ${
                  notification.is_read ? 'bg-white' : 'bg-[#FFFBEB]'
                }`}
                style={{ animationDelay: `${index * 0.05}s` }}
                onClick={() => !notification.is_read && markAsRead(notification.notification_id)}
                data-testid={`notification-${notification.notification_id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 mt-1">
                      {getIcon(notification.type)}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-[#264653] font-outfit">
                          {notification.title}
                        </h3>
                        {!notification.is_read && (
                          <Badge className="bg-[#E76F51] text-white text-xs rounded-full">
                            New
                          </Badge>
                        )}
                      </div>
                      <p className="text-[#6B7280] text-sm">{notification.message}</p>
                      <p className="text-xs text-[#6B7280] mt-2">
                        {getLocalDateTimeString(notification.created_at)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}
